import { Box, Center, Circle, Flex, Heading, Stack, Text } from '@chakra-ui/react'
import type { Dispatch, ReactElement, SetStateAction } from 'react'

import CourseQ from '@/queries/CourseQ'
import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import { useAppContext } from '@/client/context/app'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { type Query, SQL } from '@/client/query'

import {
  getSeriesQuery,
  getSessionsQuery,
  useQueryFilterBy,
  useQueryTopics,
} from '@/components/Course/helpers'
import CourseNameHeader from '@/components/CourseNameHeader'
import { SortWidget, useSort } from '@/components/Pagination/SortWidget'
import SeriesList from '@/components/Series/SeriesList'
import { useInfiniteScroll } from '@/components/Session/helpers'
import SessionCarousel from '@/components/Session/SessionCarousel/SessionCarousel'
import SessionList from '@/components/Session/SessionList'
import Spinner from '@/components/Spinner'

import CourseResultsAllHeader from './CourseResultsAllHeader'
import CourseResultsSingleEmpty from './CourseResultsSingleEmpty'
import CourseResultsSingleHeader from './CourseResultsSingleHeader'

const COURSE_OTHER_SLUG = 'other'
const COURSE_OTHER = 'Other'

export default function CourseResults({
  disabled,
  setPageDisabled,
  queryCourseSlug,
  course,
}: {
  disabled: boolean
  setPageDisabled: Dispatch<SetStateAction<boolean>>
  queryCourseSlug?: string | null
  course?: Query | null
}): ReactElement {
  const query = useQuery()
  const user = useUser()
  const appContext = useAppContext()

  const typeSelected = useQueryFilterBy()

  const queryTopics = useQueryTopics()

  const [sessionsLimit, handleLoadMoreSessions, showAllSessionsPath] = useInfiniteScroll({
    queryKey: 'sessionsLimit',
    defaultValue: user.isLoggedOut ? 8 : 24,
  })
  const [seriesLimit, handleLoadMoreSeries] = useInfiniteScroll({
    queryKey: 'seriesLimit',
    defaultValue: 4,
  })

  const seriesSortQueryKey = 'seriesSort'
  const sessionSortQueryKey = 'sessionSort'

  const seriesSorts = {
    'Starting Soon': SQL`(SELECT ${SeriesQ.countCompletedSessions()}) ASC, ${
      SessionQ.start_date
    } ASC`,
    'Starting Later': SQL`(SELECT ${SeriesQ.getComputedStartDate()}) DESC`,
    'Newly Created': SQL`${SeriesQ.created_at} DESC`,
    'Next Session': SQL`${SessionQ.start_date} ASC`,
  }
  const seriesSort = useSort(seriesSorts, seriesSortQueryKey)

  const sessionSorts = {
    'Starting Soon': SQL`${SessionQ.start_date} ASC`,
    'Newly Created': SQL`${SessionQ.created_at} DESC`,
  }
  const sessionSort = useSort(sessionSorts, sessionSortQueryKey)

  // - For "Other" courses, we want to show only featured sessions from "other"
  // - For non-Other courses, show sessions from all but "other"
  const featuredSessions =
    course == null || queryCourseSlug == null
      ? query.many`${SessionQ.getFeaturedSessions()} AND ${
          SessionQ.end_date
        } > CURRENT_TIMESTAMP ORDER BY ${SessionQ.start_date} ASC`
      : course.many`${SessionQ.getFeaturedSessions()} AND (
        CASE
            WHEN ${queryCourseSlug} = ${COURSE_OTHER_SLUG}
            THEN ${SessionQ.topic_id} IN (
                SELECT ${TopicQ.id} ${TopicQ.where(
                  { include: [{ association: 'Course', required: true }] },
                  SQL`${CourseQ.name} = ${COURSE_OTHER}`
                )}
            )
            ELSE ${SessionQ.topic_id} NOT IN (
                SELECT ${TopicQ.id} ${TopicQ.where(
                  { include: [{ association: 'Course', required: true }] },
                  SQL`${CourseQ.name} = ${COURSE_OTHER}`
                )}
            )
        END
    ) AND ${SessionQ.getComputedEndDate()} > CURRENT_TIMESTAMP ORDER BY ${SessionQ.start_date} ASC`

  const sessionsWhere = getSessionsQuery({
    courseSlug: queryCourseSlug,
    topicIds: queryTopics,
    userId: user.id,
  })

  const sessionsCount = query<number>`COUNT(*) ${sessionsWhere}` ?? 0
  const sessions = query.many`${sessionsWhere}
        ORDER BY ${sessionSort}
        LIMIT ${sessionsLimit}`

  const seriesWhere = getSeriesQuery({
    courseSlug: queryCourseSlug,
    topicIds: queryTopics,
    userId: user.id,
    isLoggedOut: user.isLoggedOut,
  })

  const seriesCount = query<number>`COUNT(*) ${seriesWhere}` ?? 0
  const series = query.many`${seriesWhere}
        ORDER BY ${seriesSort}
        LIMIT ${seriesLimit}`

  const showSessions =
    (typeSelected === 'session' || typeSelected == null) &&
    (appContext.prepass || sessionsCount > 0)

  const showSeries =
    (typeSelected === 'series' || typeSelected == null) && (appContext.prepass || seriesCount > 0)

  const courseName = course == null ? null : course(CourseQ.name)
  const courseDomain = course == null ? null : course(CourseQ.domain)
  const courseSlug = course == null ? null : course(CourseQ.slug)

  return (
    <>
      {featuredSessions.length > 0 ? (
        <Box p={0}>
          <Heading size="xl" as="h3" mb="24px">
            Featured Sessions
            <Box ml="8px" display="inline">
              ✨
            </Box>
          </Heading>
          <SessionCarousel sessions={featuredSessions} />
        </Box>
      ) : null}
      {course == null ? (
        <CourseResultsAllHeader disabled={disabled} setPageDisabled={setPageDisabled} />
      ) : (
        <CourseResultsSingleHeader
          course={course}
          disabled={disabled}
          setPageDisabled={setPageDisabled}
        />
      )}
      {disabled ? (
        <Center my="96px">
          <Spinner />
        </Center>
      ) : showSessions || showSeries ? (
        <Box mt="32px">
          {showSeries ? (
            <>
              <Flex
                align={['start', 'center']}
                direction={['column', 'row']}
                justify="space-between"
                my="24px"
              >
                <Stack direction="row" spacing="12px" align="center">
                  <Heading size="xl" as="h4">
                    <CourseNameHeader
                      courseName={courseName}
                      courseSlug={courseSlug}
                      isSubheader
                      domain={courseDomain}
                    />{' '}
                    Series
                  </Heading>
                  <Circle size="28px" bg="primary.solid">
                    <Text color="primary.contrast" fontSize="16px">
                      {seriesCount}
                    </Text>
                  </Circle>
                </Stack>
                <SortWidget sorts={seriesSorts} queryKey={seriesSortQueryKey} width="10rem" />
              </Flex>
              <Text mt={2} mb={6}>
                Join a series to learn, practice, and study{' '}
                {courseName == null ? '' : `${courseName} `}regularly with other learners like you.
              </Text>
              <SeriesList
                series={series}
                totalItemsCount={seriesCount}
                limit={seriesLimit}
                onLoadMore={handleLoadMoreSeries}
                hideHeader
              />
            </>
          ) : null}
          {showSessions ? (
            <>
              <Flex
                align={['start', 'center']}
                direction={['column', 'row']}
                justify="space-between"
                mt="40px"
                mb="24px"
              >
                <Stack direction="row" spacing="12px" align="center">
                  <Heading size="xl" as="h4">
                    <CourseNameHeader
                      courseName={courseName}
                      courseSlug={courseSlug}
                      isSubheader
                      domain={courseDomain}
                    />{' '}
                    Sessions
                  </Heading>
                  <Circle size="28px" bg="primary.solid">
                    <Text color="primary.contrast" fontSize="16px">
                      {sessionsCount}
                    </Text>
                  </Circle>
                </Stack>
                <SortWidget sorts={sessionSorts} queryKey={sessionSortQueryKey} width="10rem" />
              </Flex>
              <Text mt={2} mb={6}>
                Register for a session to review specific{' '}
                {courseName == null ? '' : `${courseName} `}topics with a small group of learners.
              </Text>
              <SessionList
                sessions={sessions}
                totalItemsCount={sessionsCount}
                limit={sessionsLimit}
                onLoadMore={handleLoadMoreSessions}
                showAllPath={showAllSessionsPath}
                hideHeader
              />
            </>
          ) : null}
        </Box>
      ) : (
        <CourseResultsSingleEmpty
          type={
            typeSelected === null
              ? 'series or sessions'
              : typeSelected === 'session'
                ? 'sessions'
                : 'series'
          }
          context={course == null ? null : course(CourseQ.name)}
          hasFilters={course != null && queryTopics.length > 0}
        />
      )}
    </>
  )
}
